import React, { useState } from "react";
import { useNavigate } from "react-router";
import StyledLoginPage from "./styles";
import { paths, API_URL } from "../../config/endpoints";
import ReactPhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { Button, LoaderComponent } from "../../components/index";
import assets from "../../assets/index";
import { useBookingEngine } from "../../context/BookingEngineContext";

const LoginScreen = () => {
  const { bookingEngineData } = useBookingEngine();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isPhoneValid, setIsPhoneValid] = useState(true);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [otpCode, setOtpCode] = useState("");
  const [isOtpStage, setIsOtpStage] = useState(false); // New state to track OTP stage

  const hostLogo = bookingEngineData?.logoImage?.urls?.big ?? assets.Logo;

  const navigate = useNavigate();

  const validatePhoneNumber = (phone: string) => {
    const phoneRegex = /^\+?[1-9]\d{1,14}$/;
    return phoneRegex.test(phone);
  };

  const handlePhoneSubmit = async () => {
    if (!validatePhoneNumber(phoneNumber)) {
      setErrorMessage("Please enter a valid phone number.");
      setIsPhoneValid(false);
      return;
    }

    setLoading(true);
    setErrorMessage(null);
    setIsPhoneValid(true);

    try {
      const response = await fetch(
        `${API_URL}/${paths.guest}/${paths.auth}/${paths.login}/${paths.challenge}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ phone: phoneNumber }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to request OTP");
      }

      setIsOtpStage(true); // Move to OTP stage
    } catch (error: any) {
      console.error("Error:", error.message);
      setErrorMessage(
        `❗ You may have provided an invalid phone number. Please try again.`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async () => {
    setLoading(true);
    setErrorMessage(null);

    try {
      const response = await fetch(
        `${API_URL}/${paths.guest}/${paths.auth}/${paths.login}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ phone: phoneNumber, otp: otpCode }),
        }
      );

      if (!response.ok) {
        throw new Error("OTP verification failed");
      }

      const data = await response.json();
      const { token, refresh_token } = data;

      localStorage.setItem("token", token);
      localStorage.setItem("refreshToken", refresh_token);

      console.log("Tokens saved, navigating to /home");

      navigate("/home", { replace: true });
    } catch (error: any) {
      console.error("Error during OTP verification:", error.message);
      setErrorMessage("Invalid OTP code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const prependPlus = (phoneNumber: string): string => {
    if (!phoneNumber.startsWith("+")) {
      return `+${phoneNumber}`;
    }
    return phoneNumber;
  };

  if (loading) {
    return <LoaderComponent content="Almost there ..." />;
  }

  console.log(isPhoneValid);

  return (
    <StyledLoginPage>
      <img
        className="logo"
        src={hostLogo || assets.Logo}
        alt={`${bookingEngineData?.name} logo`}
      />
      <div style={{ width: "30ch" }}>
        <h2>We're thrilled you are here!</h2>

        {isOtpStage ? (
          // OTP Input Stage
          <div>
            <label>Enter OTP</label>
            <input
              type="text"
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value)}
              maxLength={6} // Assuming a 6-digit OTP
              placeholder="Enter OTP code"
              style={{ width: 136 }}
            />
            <Button
              onClick={handleOtpSubmit}
              disabled={loading}
              label="Almost There"
              type="submit"
            />
            <span className="retry-span" onClick={handlePhoneSubmit}>
              Code not received? Request new code.
            </span>
          </div>
        ) : (
          // Phone Number Input Stage
          <div style={{ width: "30ch" }}>
            <label>Enter your phone number</label>
            <ReactPhoneInput
              enableSearch
              searchClass="search-bar"
              searchPlaceholder="Search country ..."
              country={"us"}
              inputClass="phone-input"
              containerClass="container-phone"
              inputProps={{
                name: "phone",
                required: true,
                style: { fontSize: 16 },
              }}
              //countryCodeEditable={false}
              value={phoneNumber}
              onChange={(phone) => setPhoneNumber(prependPlus(phone))}
              placeholder="Enter your phone number"
            />

            <Button
              onClick={handlePhoneSubmit}
              disabled={loading}
              label="Sign In"
              type="submit"
            />
          </div>
        )}

        {errorMessage && <div className="error-message">{errorMessage}</div>}
      </div>
    </StyledLoginPage>
  );
};

export default LoginScreen;
